/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Search, Filter, RotateCcw, Calendar, Landmark, BookOpen, FileText, Lock, ShieldCheck, ChevronDown, Globe, Compass, Sparkles, AlertTriangle, AlertCircle } from 'lucide-react';
import { LegalDocument, SearchFilters, ElasticSearchResult } from '../../types';
import { formatFrenchDate, formatDomain } from '../../utils/legalUtils';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { SearchAutocomplete } from '../ui/SearchAutocomplete';
import { searchService } from '../../Services';
import { useAuth } from '../../Hooks/useAuth';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { usePermissions } from '../../Hooks/usePermissions';
import { useAppSelector } from '../../ReduxToolkit/Hooks';
import { AiModeToggle, LegalQuestionInput, LegalAnalysisResult, AiAnalysisLoadingState, AiAccessDeniedModal } from '../ai';
import { aiSearchService } from '../../Services/AiSearchService';
import { AiCaseAnalysisResponse } from '../../Types/ai';

interface SearchViewProps {
  documents: LegalDocument[];
  setView: (view: string) => void;
  setActiveDocumentId: (id: number | null) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchFilters: SearchFilters;
  setSearchFilters: (filters: SearchFilters) => void;
  favorites: (string | number)[];
  toggleFavorite: (id: string | number) => void;
}

export const SearchView: React.FC<SearchViewProps> = ({
  documents,
  setView,
  setActiveDocumentId,
  searchQuery,
  setSearchQuery,
  searchFilters,
  setSearchFilters,
  favorites,
  toggleFavorite
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const PAGE_SIZE = 5;

  const location = useLocation();
  const { hasPermission } = usePermissions();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isAdmin = currentUser?.role === 'ADMIN';
  const hasAiPrivilege = hasPermission('use_ai_assistant') || isAdmin;

  // Mode Assistant Juridique IA
  const initialAiMode = Boolean(location.state?.aiMode);
  const initialAiQuestion = location.state?.aiQuestion || '';

  const [isAiMode, setIsAiMode] = React.useState(initialAiMode);
  const [aiQuestion, setAiQuestion] = React.useState(initialAiQuestion);
  const [aiAnalysisResponse, setAiAnalysisResponse] = React.useState<AiCaseAnalysisResponse | null>(null);
  const [isAiAnalyzing, setIsAiAnalyzing] = React.useState(false);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const [accessModalOpen, setAccessModalOpen] = React.useState(false);
  const [accessModalMode, setAccessModalMode] = React.useState<'IN_DEVELOPMENT' | 'UNAUTHENTICATED' | 'NO_PRIVILEGE'>('IN_DEVELOPMENT');

  const handleToggleAiMode = () => {
    setAccessModalMode('IN_DEVELOPMENT');
    setAccessModalOpen(true);
  };

  const handleRunAiAnalysis = async (questionText: string) => {
    if (!questionText.trim()) return;
    if (!isAuthenticated) {
      setAccessModalMode('UNAUTHENTICATED');
      setAccessModalOpen(true);
      return;
    }
    if (!hasAiPrivilege) {
      setAccessModalMode('NO_PRIVILEGE');
      setAccessModalOpen(true);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsAiAnalyzing(true);
    setAiError(null);

    try {
      const res = await aiSearchService.analyzeLegalCase(
        { question: questionText.trim() },
        controller.signal
      );
      setAiAnalysisResponse(res);
    } catch (err: any) {
      if (err?.name === 'AbortError' || controller.signal.aborted) {
        return;
      }
      console.error("AI Analysis Error:", err);
      setAiError(
        err?.message ||
          "Une erreur est survenue lors de l'analyse juridique. Veuillez vérifier votre connexion ou reformuler votre question."
      );
    } finally {
      setIsAiAnalyzing(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancelAiAnalysis = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsAiAnalyzing(false);
  };

  const handleNewAnalysis = () => {
    setAiAnalysisResponse(null);
    setAiError(null);
    setAiQuestion('');
  };

  const handleOpenDocumentFromAi = (documentId?: number) => {
    if (documentId) {
      navigate(`/document/${documentId}`);
    }
  };

  // Notification si l'utilisateur arrive avec le paramètre IA
  React.useEffect(() => {
    if (location.state?.aiMode) {
      setAccessModalMode('IN_DEVELOPMENT');
      setAccessModalOpen(true);
    }
  }, [location.state]);

  const [showAdvanced, setShowAdvanced] = React.useState(window.innerWidth >= 1024);
  const [currentPage, setCurrentPage] = React.useState(1);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setShowAdvanced(false);
      }
    };
    window.addEventListener('resize', handleResize);
    // Masquer initialement si on charge sur un petit ecran
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cumulative results state for "Afficher plus..."
  const [resultsList, setResultsList] = React.useState<ElasticSearchResult[]>([]);
  const [totalResults, setTotalResults] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);

  // Filter panel local states (single values)
  const [selectedNature, setSelectedNature] = React.useState<string>(searchFilters.nature || '');
  const [selectedOrigin, setSelectedOrigin] = React.useState<string>(searchFilters.legalOrigin || '');
  const [selectedStatus, setSelectedStatus] = React.useState<string>(searchFilters.legalStatus || '');
  const [selectedDomain, setSelectedDomain] = React.useState<string>(searchFilters.areaOfLaw || '');
  const [authority, setAuthority] = React.useState<string>(searchFilters.issuingAuthority || '');
  const [effectiveYear, setEffectiveYear] = React.useState<string>(searchFilters.effectiveYear ? String(searchFilters.effectiveYear) : '');
  const [keywordInput, setKeywordInput] = React.useState<string>('');
  const [availableYears, setAvailableYears] = React.useState<number[]>([]);

  React.useEffect(() => {
    searchService.getDocumentYears().then(setAvailableYears).catch(console.error);
  }, []);
  const [keywords, setKeywords] = React.useState<string[]>(searchFilters.keywords || []);

  const [dynamicNatures, setDynamicNatures] = React.useState<string[]>([]);
  const [dynamicDomains, setDynamicDomains] = React.useState<string[]>([]);
  const [dynamicOrigins, setDynamicOrigins] = React.useState<string[]>([]);

  React.useEffect(() => {
    searchService.getFiltersData().then(data => {
      setDynamicNatures(data.natures);
      setDynamicDomains(data.domains);
      setDynamicOrigins(data.origins);
    });
  }, []);

  const [localQuery, setLocalQuery] = React.useState(searchQuery);

  // Sync local query if parent changes it
  React.useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Sync with URL query parameters on arrival / change
  React.useEffect(() => {
    const urlQ = searchParams.get('q');
    const urlNature = searchParams.get('nature') || searchParams.get('category');
    const urlOrigin = searchParams.get('origin') || searchParams.get('legalOrigin');
    const urlDomain = searchParams.get('domain') || searchParams.get('areaOfLaw');
    const urlStatus = searchParams.get('status') || searchParams.get('legalStatus');
    const urlYear = searchParams.get('year') || searchParams.get('effectiveYear');

    if (urlQ !== null && urlQ !== searchQuery) {
      setSearchQuery(urlQ);
      setLocalQuery(urlQ);
    }

    if (isAuthenticated) {
      const updates: Partial<SearchFilters> = {};
      if (urlNature) updates.nature = urlNature;
      if (urlOrigin) updates.legalOrigin = urlOrigin;
      if (urlDomain) updates.areaOfLaw = urlDomain;
      if (urlStatus) updates.legalStatus = urlStatus;
      if (urlYear) updates.effectiveYear = Number(urlYear);

      if (Object.keys(updates).length > 0) {
        setSearchFilters({ ...searchFilters, ...updates });
      }
    }
  }, [searchParams, isAuthenticated]);

  // Sync local state when parent filters change
  React.useEffect(() => {
    setSelectedNature(searchFilters.nature || '');
    setSelectedOrigin(searchFilters.legalOrigin || '');
    setSelectedStatus(searchFilters.legalStatus || '');
    setSelectedDomain(searchFilters.areaOfLaw || '');
    setAuthority(searchFilters.issuingAuthority || '');
    setEffectiveYear(searchFilters.effectiveYear ? String(searchFilters.effectiveYear) : '');
    setKeywords(searchFilters.keywords || []);
  }, [searchFilters]);

  // ── Main search effect (Page 1 / Nouvelles recherches ou chargement initial) ─
  React.useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    const abortController = new AbortController();
    
    setIsSearching(true);
    setSearchError(null);
    setCurrentPage(1);

    // Seul un utilisateur connecté peut appliquer des filtres
    const effectiveFilters = isAuthenticated ? searchFilters : {};

    searchService.globalSearch(
      trimmedQuery,
      effectiveFilters,
      1,
      PAGE_SIZE,
      abortController.signal
    )
      .then(result => {
        setResultsList(result.data);
        setTotalResults(result.total);
        setHasMore(result.hasMore ?? (result.data.length < result.total));
        setIsSearching(false);
      })
      .catch((err: any) => {
        if (err.name !== 'AbortError') {
          setSearchError('Erreur lors de la recherche. Vérifiez votre connexion internet');
          setIsSearching(false);
        }
      });

    return () => abortController.abort();
  }, [searchQuery, searchFilters, isAuthenticated]);

  // ── Handler "Afficher plus..." ──────────────────────────────────────────────
  const handleLoadMore = async () => {
    if (isFetchingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    setIsFetchingMore(true);

    try {
      const result = await searchService.globalSearch(
        searchQuery.trim(),
        isAuthenticated ? searchFilters : {},
        nextPage,
        PAGE_SIZE
      );

      setResultsList(prev => {
        const updated = [...prev, ...result.data];
        setHasMore(result.hasMore ?? (updated.length < result.total));
        return updated;
      });
      setCurrentPage(nextPage);
      setTotalResults(result.total);
    } catch (err) {
      console.error('Erreur lors du chargement des résultats suivants', err);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const updateFilterAndSearch = (updates: Partial<SearchFilters>) => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    const newFilters = { ...searchFilters, ...updates };
    Object.keys(newFilters).forEach(key => {
      const k = key as keyof SearchFilters;
      if (!newFilters[k] || (Array.isArray(newFilters[k]) && newFilters[k]?.length === 0)) {
        delete newFilters[k];
      }
    });
    setSearchFilters(newFilters);
  };

  const handleApplyFilters = () => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    updateFilterAndSearch({
      nature: selectedNature || undefined,
      legalOrigin: selectedOrigin || undefined,
      legalStatus: selectedStatus || undefined,
      areaOfLaw: selectedDomain || undefined,
      issuingAuthority: authority.trim() || undefined,
      effectiveYear: effectiveYear ? Number(effectiveYear) : undefined,
      keywords: keywords.length > 0 ? keywords : undefined
    });
  };

  const handleResetFilters = () => {
    setSelectedNature('');
    setSelectedOrigin('');
    setSelectedStatus('');
    setSelectedDomain('');
    setAuthority('');
    setEffectiveYear('');
    setKeywords([]);
    setKeywordInput('');
    setSearchFilters({});
    setSearchQuery('');
  };

  const handleNewSearch = (term: string) => {
    setSelectedNature('');
    setSelectedOrigin('');
    setSelectedStatus('');
    setSelectedDomain('');
    setAuthority('');
    setEffectiveYear('');
    setKeywords([]);
    setKeywordInput('');
    setSearchFilters({});
    setSearchQuery(term);
  };

  const addKeyword = () => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      const newKw = [...keywords, keywordInput.trim()];
      setKeywords(newKw);
      setKeywordInput('');
      updateFilterAndSearch({ keywords: newKw });
    }
  };

  const removeKeyword = (kw: string) => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    const newKw = keywords.filter(k => k !== kw);
    setKeywords(newKw);
    updateFilterAndSearch({ keywords: newKw });
  };

  // Render the highlighted title safely
  const renderTitleHtml = (result: ElasticSearchResult) => {
    const html = result.titleHighlight || result.officialTitle;
    return (
      <span
        dangerouslySetInnerHTML={{ __html: html }}
        className="[&_strong]:font-extrabold [&_strong]:bg-yellow-100 [&_strong]:rounded-sm [&_strong]:px-0.5"
      />
    );
  };

  const natures = dynamicNatures.length > 0 ? dynamicNatures : ['CONSTITUTION', 'LOI', 'ORDONNANCE', 'DECRET', 'ARRETE', 'CIRCULAIRE', 'DECISION', 'OHADA', 'CEMAC', 'COBAC', 'TRAITE', 'DIRECTIVE'];

  const normalizeStr = (str: string) =>
    str
      ? str
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toUpperCase()
          .trim()
      : '';

  // Résolution automatique et résiliente de la nature/catégorie sélectionnée par défaut (accents, pluriel, casse)
  const activeNatureValue = React.useMemo(() => {
    if (!selectedNature) return '';
    const normSelected = normalizeStr(selectedNature).replace(/S$/, '');

    // 1. Match exact insensible à la casse
    const exact = natures.find(n => n.toUpperCase() === selectedNature.toUpperCase());
    if (exact) return exact;

    // 2. Match sans accents et sans 's' final (ex: DECRET vs DÉCRET, LOIS vs LOI)
    const matched = natures.find(n => {
      const normN = normalizeStr(n).replace(/S$/, '');
      return normN === normSelected || normN.startsWith(normSelected) || normSelected.startsWith(normN);
    });
    if (matched) return matched;

    return selectedNature;
  }, [selectedNature, natures]);

  // Garantir que la valeur par défaut est toujours présente dans la liste déroulante
  const displayNatures = React.useMemo(() => {
    if (!activeNatureValue) return natures;
    const exists = natures.some(n => n.toUpperCase() === activeNatureValue.toUpperCase());
    if (!exists) {
      return [activeNatureValue, ...natures];
    }
    return natures;
  }, [natures, activeNatureValue]);
  
  const statuses = [
    { id: 'EnVigueur', label: 'En vigueur' },
    { id: 'Modifie', label: 'Modifié' },
    { id: 'Abroge', label: 'Abrogé' }
  ];

  const isStatusActive = (stId: string) => {
    if (!selectedStatus) return false;
    const cur = selectedStatus.trim().toLowerCase();
    const target = stId.toLowerCase();
    if (cur === target) return true;
    if (target.includes('vig') && (cur.includes('vig') || cur.includes('cours'))) return true;
    if (target.includes('modif') && cur.includes('modif')) return true;
    if (target.includes('abrog') && cur.includes('abrog')) return true;
    return false;
  };
  const fallbackDomains = [
    'Droit civil', 'Droit pénal', 'Droit commercial', 'Droit des affaires', 'Droit fiscal',
    'Droit social', 'Droit administratif', 'Droit constitutionnel', 'Droit foncier',
    'Droit minier', "Droit de l'environnement", 'Droit douanier', 'Droit bancaire',
    'Droit OHADA', 'Droit international', 'Droit du travail', 'Droit de la santé'
  ];

  const legalDomains = React.useMemo(() => {
    const rawList = dynamicDomains.length > 0 ? dynamicDomains : fallbackDomains;
    const unique = new Map<string, { id: string; label: string }>();

    rawList.forEach(item => {
      if (!item || !item.trim()) return;
      const cleanLabel = formatDomain(item.trim());
      const key = cleanLabel.toLowerCase();
      if (!unique.has(key)) {
        unique.set(key, { id: item.trim(), label: cleanLabel });
      }
    });

    return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [dynamicDomains]);

  const selectedDomainId = React.useMemo(() => {
    if (!selectedDomain) return '';
    const norm = selectedDomain.trim().toLowerCase().replace(/_/g, ' ');
    const found = legalDomains.find(d => 
      d.id.toLowerCase() === selectedDomain.toLowerCase() ||
      d.label.toLowerCase() === selectedDomain.toLowerCase() ||
      d.id.toLowerCase().replace(/_/g, ' ') === norm ||
      d.label.toLowerCase().replace(/_/g, ' ') === norm
    );
    return found ? found.id : selectedDomain;
  }, [selectedDomain, legalDomains]);

  const displayDomains = React.useMemo(() => {
    if (!selectedDomainId) return legalDomains;
    const exists = legalDomains.some(d => d.id.toLowerCase() === selectedDomainId.toLowerCase());
    if (!exists) {
      return [{ id: selectedDomainId, label: formatDomain(selectedDomainId) }, ...legalDomains];
    }
    return legalDomains;
  }, [legalDomains, selectedDomainId]);

  // Déduplication et standardisation sans doublon des sources (National, Communautaire, Internationale)
  const availableOrigins = React.useMemo(() => {
    const pillars = [
      { id: 'NATIONAL', label: 'NATIONAL', icon: Landmark, patterns: ['NATION', 'CAMEROUN'] },
      { id: 'COMMUNITY', label: 'COMMUNAUTAIRE', icon: Globe, patterns: ['COMMUN', 'REGION', 'CEMAC', 'OHADA', 'COBAC'] },
      { id: 'INTERNATIONAL', label: 'INTERNATIONALE', icon: Compass, patterns: ['INTERNAT'] }
    ];

    if (!dynamicOrigins || dynamicOrigins.length === 0) {
      return pillars;
    }

    const customOrigins: typeof pillars = [];

    dynamicOrigins.forEach(raw => {
      if (!raw || !raw.trim()) return;
      const upper = raw.trim().toUpperCase();
      const isInternat = upper.includes('INTERNAT');
      const isNation = !isInternat && (upper.includes('NATION') || upper.includes('CAMEROUN'));
      const isCommun = upper.includes('COMMUN') || upper.includes('REGION') || upper.includes('CEMAC') || upper.includes('OHADA') || upper.includes('COBAC');

      if (!isInternat && !isNation && !isCommun) {
        if (!customOrigins.some(c => c.id.toUpperCase() === raw.trim().toUpperCase())) {
          customOrigins.push({
            id: raw.trim(),
            label: raw.trim(),
            icon: BookOpen,
            patterns: [raw.trim().toUpperCase()]
          });
        }
      }
    });

    // Toujours conserver les 3 piliers fondamentaux : National, Communautaire, Internationale
    return [...pillars, ...customOrigins];
  }, [dynamicOrigins]);

  const isOriginActive = (origItem: { id: string, patterns: string[] }) => {
    if (!selectedOrigin) return false;
    const rawVal = Array.isArray(selectedOrigin) ? selectedOrigin[0] : selectedOrigin;
    if (!rawVal) return false;
    const curUpper = String(rawVal).trim().toUpperCase();
    if (curUpper === origItem.id.toUpperCase()) return true;

    if (origItem.id === 'INTERNATIONAL') {
      return curUpper.includes('INTERNAT');
    }
    if (origItem.id === 'NATIONAL') {
      // Exclure formellement l'international car "INTERNATIONAL" contient la sous-chaîne "NATION" !
      if (curUpper.includes('INTERNAT')) return false;
      return curUpper.includes('NATION') || curUpper.includes('CAMEROUN');
    }
    if (origItem.id === 'COMMUNITY') {
      return origItem.patterns.some(pat => curUpper.includes(pat));
    }
    return origItem.patterns.some(pat => curUpper === pat);
  };

  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (selectedNature) count++;
    if (selectedOrigin) count++;
    if (selectedStatus) count++;
    if (selectedDomain) count++;
    if (effectiveYear) count++;
    if (keywords.length > 0) count += keywords.length;
    return count;
  }, [selectedNature, selectedOrigin, selectedStatus, selectedDomain, effectiveYear, keywords]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4 animate-fade-in text-left">
      
      {/* ── Top Bar Container : Input de recherche / Textarea + Barre d'outils ─────── */}
      <div className="max-w-4xl space-y-2.5">
        {isAiMode ? (
          <LegalQuestionInput
            value={aiQuestion}
            onChange={setAiQuestion}
            onSubmit={handleRunAiAnalysis}
            isLoading={isAiAnalyzing}
            onCancel={handleCancelAiAnalysis}
            variant="search"
          />
        ) : (
          <div className="max-w-2xl">
            <SearchAutocomplete
              id="search-view-bar"
              value={localQuery}
              onChange={setLocalQuery}
              onSearch={handleNewSearch}
              documents={documents}
              placeholder="Rechercher par expression libre (ex: 'licenciement', 'bail commercial', 'décret')..."
              variant="search"
            />
          </div>
        )}

        {/* ── Sub-bar Google-style : Mode IA + Filtres sur la MÊME LIGNE ─────── */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1 pb-2 border-b border-zinc-200/70 max-w-4xl">
          <div className="text-xs text-zinc-500 font-normal">
            {isSearching && !isAiMode && (
              <span className="flex items-center gap-1.5 text-zinc-500">
                <span className="w-3 h-3 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin"></span>
                <span>Recherche en cours...</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Bouton Toggle Mode IA */}
            <AiModeToggle
              isAiMode={isAiMode}
              onToggle={handleToggleAiMode}
              variant="search"
            />

            {/* Boutons Filtres & Reset (actifs en mode recherche documentaire) */}
            {!isAiMode && (
              <>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all cursor-pointer ${
                    showAdvanced
                      ? 'bg-zinc-900 text-white border-zinc-900 shadow-xs'
                      : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300'
                  }`}
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span>Filtres</span>
                  {activeFiltersCount > 0 && (
                    <span className={`w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center ${
                      showAdvanced ? 'bg-white text-zinc-900' : 'bg-slate-900 text-white'
                    }`}>
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                {activeFiltersCount > 0 && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    title="Réinitialiser les filtres"
                    className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-full border border-zinc-200 bg-white transition-all cursor-pointer"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── SECTION RÉSULTATS : Mode IA vs Mode Documentaire ─────────────────────── */}
      {isAiMode ? (
        <div className="w-full max-w-4xl space-y-6 pt-2">
          {/* État de chargement IA */}
          {isAiAnalyzing && (
            <AiAnalysisLoadingState onCancel={handleCancelAiAnalysis} />
          )}

          {/* Erreur de requête IA */}
          {aiError && !isAiAnalyzing && (
            <div className="p-5 rounded-2xl bg-red-50 border border-red-200 text-xs text-red-800 space-y-3 shadow-xs">
              <div className="flex items-center gap-2 font-bold text-sm text-red-900">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span>Impossible de finaliser l'analyse juridique</span>
              </div>
              <p className="leading-relaxed">{aiError}</p>
              <button
                type="button"
                onClick={() => handleRunAiAnalysis(aiQuestion)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition cursor-pointer border-none shadow-xs"
              >
                Réessayer l'analyse
              </button>
            </div>
          )}

          {/* Résultat de l'analyse IA (6 blocs) */}
          {aiAnalysisResponse && !isAiAnalyzing && (
            <LegalAnalysisResult
              response={aiAnalysisResponse}
              onNewAnalysis={handleNewAnalysis}
              onOpenDocument={handleOpenDocumentFromAi}
            />
          )}

          {/* État initial invitant si aucune analyse n'est encore lancée */}
          {!isAiAnalyzing && !aiAnalysisResponse && !aiError && (
            <div className="p-8 sm:p-10 rounded-3xl bg-white border border-zinc-200/90 shadow-xs text-center space-y-4 max-w-2xl mx-auto mt-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-50 text-primary-750 border border-violet-100 flex items-center justify-center mx-auto shadow-2xs">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="space-y-1.5">
                <h3 className="font-display text-base sm:text-lg font-extrabold text-zinc-900">
                  Assistant Juridique IA Docarn
                </h3>
                <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed max-w-lg mx-auto">
                  Décrivez votre problématique ou question de droit dans le champ ci-dessus.
                  L'assistant identifiera automatiquement les qualifications, les textes législatifs applicables et les démarches à entreprendre.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* MODE DOCUMENTAIRE CLASSIQUE */
        <>
          {/* ── Chips des Filtres Actifs (Google-style) ────────────────────────── */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5 pb-1 max-w-4xl">
          <span className="text-[11px] text-zinc-400 font-medium mr-1">Filtres :</span>
          {selectedNature && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs rounded-full font-medium transition-colors">
              <span>Nature : <strong>{activeNatureValue}</strong></span>
              <button 
                type="button"
                onClick={() => {
                  setSelectedNature('');
                  updateFilterAndSearch({ nature: undefined });
                }} 
                className="text-zinc-400 hover:text-zinc-800 font-bold cursor-pointer"
              >
                ×
              </button>
            </span>
          )}
          {selectedOrigin && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs rounded-full font-medium transition-colors">
              <span>Source : <strong>{selectedOrigin}</strong></span>
              <button 
                type="button"
                onClick={() => {
                  setSelectedOrigin('');
                  updateFilterAndSearch({ legalOrigin: undefined });
                }} 
                className="text-zinc-400 hover:text-zinc-800 font-bold cursor-pointer"
              >
                ×
              </button>
            </span>
          )}
          {selectedStatus && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs rounded-full font-medium transition-colors">
              <span>Statut : <strong>{selectedStatus}</strong></span>
              <button 
                type="button"
                onClick={() => {
                  setSelectedStatus('');
                  updateFilterAndSearch({ legalStatus: undefined });
                }} 
                className="text-zinc-400 hover:text-zinc-800 font-bold cursor-pointer"
              >
                ×
              </button>
            </span>
          )}
          {selectedDomain && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs rounded-full font-medium transition-colors">
              <span>Domaine : <strong>{formatDomain(selectedDomain)}</strong></span>
              <button 
                type="button"
                onClick={() => {
                  setSelectedDomain('');
                  updateFilterAndSearch({ areaOfLaw: undefined });
                }} 
                className="text-zinc-400 hover:text-zinc-800 font-bold cursor-pointer"
              >
                ×
              </button>
            </span>
          )}
          {effectiveYear && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs rounded-full font-medium transition-colors">
              <span>Année : <strong>{effectiveYear}</strong></span>
              <button 
                type="button"
                onClick={() => {
                  setEffectiveYear('');
                  updateFilterAndSearch({ effectiveYear: undefined });
                }} 
                className="text-zinc-400 hover:text-zinc-800 font-bold cursor-pointer"
              >
                ×
              </button>
            </span>
          )}
          {keywords.map(kw => (
            <span key={kw} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs rounded-full font-medium transition-colors">
              <span>Mot-clé : <strong>{kw}</strong></span>
              <button 
                type="button"
                onClick={() => removeKeyword(kw)} 
                className="text-zinc-400 hover:text-zinc-800 font-bold cursor-pointer"
              >
                ×
              </button>
            </span>
          ))}
          <button 
            type="button"
            onClick={handleResetFilters} 
            className="text-[11px] text-zinc-500 hover:text-red-600 underline font-medium ml-1 cursor-pointer"
          >
            Effacer tout
          </button>
        </div>
      )}

      {/* ── Main Layout (Filtres à gauche si ouverts, Résultats à droite) ─ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pt-2">
        
        {/* SIDEBAR FILTERS (Compact & Digeste) */}
        {showAdvanced && (
          <div className="lg:col-span-4 xl:col-span-3.5 space-y-4 animate-fade-in">
            <div className="bg-white p-5 rounded-2xl border border-zinc-200/80 shadow-xs space-y-5">
              <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                <span className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5 text-zinc-500" /> Options de recherche
                </span>
                {!isAuthenticated && (
                  <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3 text-amber-600" /> Abonnés
                  </span>
                )}
              </div>

              {/* Alerte non connecté */}
              {!isAuthenticated && (
                <div className="p-3 bg-amber-50/80 border border-amber-200/70 rounded-xl space-y-1.5 animate-fade-in text-xs">
                  <div className="flex items-center gap-1.5 text-amber-900 font-bold">
                    <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span>Filtres réservés aux abonnés</span>
                  </div>
                  <p className="text-[11px] text-amber-800 leading-relaxed font-normal">
                    Connectez-vous pour filtrer les documents par nature, source, statut, domaine et année.
                  </p>
                  <button
                    type="button"
                    onClick={() => openAuthModal()}
                    className="w-full mt-1 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all shadow-xs cursor-pointer"
                  >
                    Se connecter / S'abonner
                  </button>
                </div>
              )}

              {/* Nature */}
              <div className="space-y-1.5">
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span>Nature de l'acte</span>
                  {!isAuthenticated && <Lock className="w-3 h-3 text-zinc-400" />}
                </label>
                <select
                  disabled={!isAuthenticated}
                  value={activeNatureValue}
                  onChange={e => {
                    if (!isAuthenticated) {
                      openAuthModal();
                      return;
                    }
                    setSelectedNature(e.target.value);
                    updateFilterAndSearch({ nature: e.target.value || undefined });
                  }}
                  className={`w-full p-2 border rounded-xl text-xs font-semibold focus:outline-none transition-colors ${
                    !isAuthenticated 
                      ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60' 
                      : 'bg-zinc-50/80 border-zinc-200 text-zinc-700 focus:border-slate-400 cursor-pointer'
                  }`}
                >
                  <option value="">-- Toutes les natures --</option>
                  {displayNatures.map(n => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              {/* Source (Origin) */}
              <div className="space-y-1.5">
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span>Source juridique</span>
                  {!isAuthenticated && <Lock className="w-3 h-3 text-zinc-400" />}
                </label>
                <div className="flex flex-col gap-1.5">
                  {availableOrigins.map(origItem => {
                    const active = isOriginActive(origItem);
                    const Icon = origItem.icon;
                    return (
                      <button
                        key={origItem.id}
                        type="button" 
                        disabled={!isAuthenticated}
                        onClick={() => {
                          if (!isAuthenticated) {
                            openAuthModal();
                            return;
                          }
                          const newOrig = active ? '' : origItem.id;
                          setSelectedOrigin(newOrig);
                          updateFilterAndSearch({ legalOrigin: newOrig || undefined });
                        }}
                        className={`w-full text-left p-2.5 rounded-xl border text-xs font-semibold transition-all flex items-center justify-between ${
                          !isAuthenticated
                            ? 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed opacity-60'
                            : active 
                              ? 'bg-slate-900 text-white border-slate-900 shadow-xs cursor-pointer' 
                              : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 cursor-pointer'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 opacity-80" />
                          <span>{origItem.label}</span>
                        </span>
                        {active && isAuthenticated && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Statut */}
              <div className="space-y-1.5">
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span>Statut légal</span>
                  {!isAuthenticated && <Lock className="w-3 h-3 text-zinc-400" />}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {statuses.map(st => {
                    const active = isStatusActive(st.id);
                    return (
                      <button
                        key={st.id}
                        type="button" 
                        disabled={!isAuthenticated}
                        onClick={() => {
                          if (!isAuthenticated) {
                            openAuthModal();
                            return;
                          }
                          const newSt = active ? '' : st.id;
                          setSelectedStatus(newSt);
                          updateFilterAndSearch({ legalStatus: newSt || undefined });
                        }}
                        className={`px-2.5 py-1 text-[10px] rounded-lg border font-bold uppercase transition-all ${
                          !isAuthenticated
                            ? 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed opacity-60'
                            : active 
                              ? 'bg-zinc-900 text-white border-zinc-900 shadow-xs cursor-pointer' 
                              : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-100 cursor-pointer'
                        }`}
                      >
                        {st.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Domaine du Droit */}
              <div className="space-y-1.5">
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span>Domaine du Droit</span>
                  {!isAuthenticated && <Lock className="w-3 h-3 text-zinc-400" />}
                </label>
                <select
                  disabled={!isAuthenticated}
                  value={selectedDomainId}
                  onChange={e => {
                    if (!isAuthenticated) {
                      openAuthModal();
                      return;
                    }
                    const val = e.target.value;
                    setSelectedDomain(val);
                    updateFilterAndSearch({ areaOfLaw: val || undefined });
                  }}
                  className={`w-full p-2 border rounded-xl text-xs font-semibold focus:outline-none transition-colors ${
                    !isAuthenticated
                      ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60'
                      : 'bg-zinc-50/80 border-zinc-200 text-zinc-700 focus:border-slate-400 cursor-pointer'
                  }`}
                >
                  <option value="">-- Tous les domaines --</option>
                  {displayDomains.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Année d'adoption */}
              <div className="space-y-1.5">
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span>Année d'adoption</span>
                  {!isAuthenticated && <Lock className="w-3 h-3 text-zinc-400" />}
                </label>
                <select
                  disabled={!isAuthenticated}
                  value={effectiveYear}
                  onChange={e => {
                    if (!isAuthenticated) {
                      openAuthModal();
                      return;
                    }
                    setEffectiveYear(e.target.value);
                    updateFilterAndSearch({ effectiveYear: e.target.value ? Number(e.target.value) : undefined });
                  }}
                  className={`w-full p-2 border rounded-xl text-xs font-semibold focus:outline-none transition-colors ${
                    !isAuthenticated
                      ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60'
                      : 'bg-zinc-50/80 border-zinc-200 text-zinc-700 focus:border-slate-400 cursor-pointer'
                  }`}
                >
                  <option value="">-- Toutes les années --</option>
                  {availableYears.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mots-clés */}
              <div className="space-y-1.5">
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span>Mots-clés Juridiques</span>
                  {!isAuthenticated && <Lock className="w-3 h-3 text-zinc-400" />}
                </label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    disabled={!isAuthenticated}
                    placeholder="Ex: responsabilité"
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addKeyword()}
                    className={`flex-1 p-2 border rounded-xl text-xs focus:outline-none ${
                      !isAuthenticated
                        ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60'
                        : 'bg-zinc-50/80 border-zinc-200 text-zinc-700 focus:border-slate-400'
                    }`}
                  />
                  <button
                    type="button"
                    disabled={!isAuthenticated}
                    onClick={addKeyword}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
                      !isAuthenticated
                        ? 'bg-zinc-100 text-zinc-400 border border-zinc-200 cursor-not-allowed opacity-60'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer'
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Bouton Appliquer */}
              <button
                disabled={!isAuthenticated}
                onClick={handleApplyFilters}
                className={`w-full font-bold uppercase tracking-wider text-xs py-2.5 rounded-xl transition-all border-none shadow-xs ${
                  isAuthenticated 
                    ? 'bg-slate-900 hover:bg-slate-800 text-white cursor-pointer shadow-sm' 
                    : 'bg-zinc-200 text-zinc-400 cursor-not-allowed opacity-70'
                }`}
              >
                {isAuthenticated ? "Appliquer les Filtres" : "Filtres réservés aux abonnés"}
              </button>
            </div>
          </div>
        )}

        {/* RESULTS COLUMN (Google-style max-w-3xl) */}
        <div className={showAdvanced ? "lg:col-span-8 xl:col-span-8.5 space-y-4 max-w-3xl" : "lg:col-span-12 space-y-4 max-w-3xl"}>
          
          {/* Error state */}
          {searchError && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-xl text-xs text-red-700 font-medium text-center">
              {searchError}
            </div>
          )}

          {/* Loading state (initial search) */}
          {isSearching && (
            <div className="space-y-5 pt-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="py-3 animate-pulse space-y-2">
                  <div className="h-3 bg-zinc-200/80 rounded-full w-1/4"></div>
                  <div className="h-5 bg-zinc-200 rounded-full w-4/5"></div>
                  <div className="h-3.5 bg-zinc-100 rounded-full w-full"></div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isSearching && !searchError && totalResults === 0 && (
            <div className="py-12 text-left space-y-3">
              <h3 className="text-zinc-900 font-medium text-base">
                Aucun résultat ne correspond aux termes de recherche spécifiés.
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed max-w-lg">
                Suggestions :
                <br />• Vérifiez l’orthographe des termes de recherche.
                <br />• Essayez d'autres mots ou des termes plus généraux.
                <br />• Essayez de désactiver certains filtres.
              </p>
              {activeFiltersCount > 0 && (
                <button 
                  onClick={handleResetFilters} 
                  className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-700 transition-colors cursor-pointer"
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          )}

          {/* Results list Google-style */}
          {!isSearching && resultsList.length > 0 && (
            <div className="divide-y divide-zinc-100/90">
              {resultsList.map((res, idx) => {
                const isLocked = res.isLocked ?? !isAuthenticated;

                return (
                  <div
                    key={res.id || idx}
                    className="py-3.5 sm:py-4 group"
                  >
                    {/* Breadcrumbs / Header métadonnées façon Google */}
                    {isLocked ? (
                      <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1">
                        <span className="inline-flex items-center gap-1 bg-slate-50 text-slate-700 px-2 py-0.5 rounded text-[10px] font-semibold border border-slate-200">
                          <Lock className="w-3 h-3 text-slate-500" /> Référence réservée
                        </span>
                        {res.hasDate && (
                          <span className="text-[11px] text-zinc-400">
                            • Date disponible
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-zinc-600 mb-1">
                        <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                          <BookOpen className="w-2.5 h-2.5 text-slate-600" />
                        </span>
                        <span className="font-semibold text-zinc-800">{res.categoryName || 'Texte Juridique'}</span>
                        {res.referenceNumber && (
                          <>
                            <span className="text-zinc-400">›</span>
                            <span className="font-mono text-[11px] text-zinc-600">{res.referenceNumber}</span>
                          </>
                        )}
                        {res.dateOfAdoption && (
                          <>
                            <span className="text-zinc-400">›</span>
                            <span className="text-zinc-500 text-[11px]">{formatFrenchDate(res.dateOfAdoption)}</span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Titre officiel style Google (#1a0dab hover:underline) */}
                    <h3
                      className="text-[17px] sm:text-[19px] text-[#1a0dab] group-hover:underline cursor-pointer font-normal sm:font-medium leading-snug mb-1"
                      onClick={() => {
                        if (isLocked) {
                          openAuthModal();
                        } else {
                          navigate(`/document/${res.id}`, { state: { document: res, searchQuery } });
                        }
                      }}
                    >
                      {renderTitleHtml(res)}
                    </h3>

                    {/* Zone de contenu / Extraits contextuels style Google */}
                    {isLocked ? (
                      <div
                        onClick={openAuthModal}
                        className="mt-1 p-3 bg-slate-50/80 border border-slate-200/80 rounded-xl cursor-pointer hover:border-slate-300 hover:bg-slate-100/70 transition-all group/card space-y-1.5 max-w-2xl"
                      >
                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold text-slate-600">
                          {res.hasSummary && (
                            <span className="inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded border border-slate-200">
                              <FileText className="w-3 h-3 text-slate-600" /> Articles indexés
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded border border-slate-200">
                            <BookOpen className="w-3 h-3 text-slate-600" /> Texte intégral disponible
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-slate-700 font-medium">
                          <span className="flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <span>Connectez-vous pour débloquer le texte officiel</span>
                          </span>
                          <span className="text-[11px] text-primary-750 font-bold underline shrink-0 ml-2">
                            Consulter →
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[13px] sm:text-[14px] text-[#4d5156] leading-relaxed max-w-2xl">
                        {res.bestMatch?.highlight ? (
                          <div
                            className="line-clamp-2 [&_strong]:font-bold [&_strong]:text-zinc-900 [&_strong]:bg-yellow-100 [&_strong]:px-0.5 [&_strong]:rounded-xs"
                            dangerouslySetInnerHTML={{ __html: res.bestMatch.highlight }}
                          />
                        ) : (
                          <p className="line-clamp-2">
                            {res.areaOfLaw && `Domaine : ${formatDomain(res.areaOfLaw)}. `}
                            {res.legalOrigin && `Origine : ${res.legalOrigin.replace('_', ' ')}. `}
                            {res.issuingAuthority && `Émetteur : ${res.issuingAuthority}. `}
                            {!res.areaOfLaw && !res.legalOrigin && !res.issuingAuthority && "Texte juridique officiel indexé et consultable."}
                          </p>
                        )}

                        {/* Badges discrets sous le texte */}
                        <div className="flex flex-wrap items-center gap-2 mt-1.5 pt-0.5 text-[11px] text-zinc-500">
                          {res.legalStatus && (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                              res.legalStatus.toLowerCase().includes('vig') 
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
                                : res.legalStatus.toLowerCase().includes('modif')
                                ? 'bg-amber-50 text-amber-700 border border-amber-200/60'
                                : 'bg-zinc-100 text-zinc-600 border border-zinc-200/60'
                            }`}>
                              {res.legalStatus}
                            </span>
                          )}
                          {res.effectiveYear && (
                            <span className="text-[11px] text-zinc-500">
                              • Adoption : {res.effectiveYear}
                            </span>
                          )}
                          {res.totalPages && (
                            <span className="text-[11px] text-zinc-400">
                              • {res.totalPages} page{res.totalPages > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Bouton "Afficher plus d'actes" style Google */}
          {hasMore && !isSearching && (
            <div className="pt-6 pb-12">
              <button
                type="button"
                disabled={isFetchingMore}
                onClick={handleLoadMore}
                className="inline-flex items-center gap-2 px-6 py-2 bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 text-zinc-800 font-semibold text-xs rounded-full transition-all cursor-pointer disabled:opacity-50"
              >
                {isFetchingMore ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-zinc-400 border-t-zinc-800 rounded-full animate-spin"></span>
                    <span>Chargement...</span>
                  </>
                ) : (
                  <>
                    <span>Afficher plus de résultats</span>
                    <ChevronDown className="w-3.5 h-3.5 text-zinc-600" />
                  </>
                )}
              </button>
            </div>
          )}

        </div>
      </div>
        </>
      )}

      {/* Modal de contrôle d'accès IA */}
      <AiAccessDeniedModal
        isOpen={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        mode={accessModalMode}
        onLogin={openAuthModal}
      />
    </div>
  );
};
